// src/lib/employerRepo.ts
import { ensureDb } from "./firestore/ensureDb";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  writeBatch,
} from "firebase/firestore";
import type {
  EmployerProfile,
  Organization,
  Job,
  JobApplication,
} from "../types/firestore";
import { sanitizeJobPayloadForFirestore } from "./jobs/jobFirestoreSanitize";

export async function getEmployerProfile(uid: string): Promise<EmployerProfile | null> {
  const firestoreDb = ensureDb();
  const snap = await getDoc(doc(firestoreDb, "profiles", uid));
  if (!snap.exists()) return null;
  const data = snap.data() as any;
  if (data.role !== "employer") return null;
  return { uid, ...data } as EmployerProfile;
}

export async function upsertEmployerProfile(uid: string, data: Partial<EmployerProfile>) {
  const firestoreDb = ensureDb();
  // CRITICAL: Always set role="employer" explicitly, even if data doesn't include it
  // This prevents any default role logic from overwriting employer role
  await setDoc(
    doc(firestoreDb, "profiles", uid),
    { uid, role: "employer", ...data, updatedAt: serverTimestamp() },
    { merge: true }
  );
  if (import.meta.env.DEV) console.log('[upsertEmployerProfile] Profile updated with role=employer for uid:', uid);
}

export async function createOrganization(
  ownerUid: string,
  org: Omit<Organization, "id" | "createdAt" | "updatedAt" | "createdBy">
): Promise<Organization> {
  const firestoreDb = ensureDb();
  const ref = await addDoc(collection(firestoreDb, "organizations"), {
    ...org,
    createdBy: ownerUid,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  const result: Organization = { id: ref.id, ...org, createdBy: ownerUid };
  await upsertEmployerProfile(ownerUid, { orgId: ref.id });
  return result;
}

export async function getOrganization(orgId: string): Promise<Organization | null> {
  const firestoreDb = ensureDb();
  const snap = await getDoc(doc(firestoreDb, "organizations", orgId));
  if (!snap.exists()) return null;
  return { id: snap.id, ...(snap.data() as any) } as Organization;
}

/**
 * Resolve employer orgId from profile
 * Returns orgId string or null if cannot resolve
 * Falls back to authUid if profile.orgId is missing (legacy: orgId == auth.uid)
 */
export async function resolveEmployerOrgId(authUid: string): Promise<string | null> {
  try {
    const profile = await getEmployerProfile(authUid);
    if (!profile || profile.role !== "employer") {
      return null;
    }
    // If orgId exists, use it
    if (profile.orgId) {
      return profile.orgId;
    }
    // Legacy fallback: orgId == auth.uid (for V1 rules that use orgId == auth.uid)
    return authUid;
  } catch (err) {
    console.warn("[resolveEmployerOrgId] Error:", err);
    return null;
  }
}

/**
 * Ensure organization document exists (non-blocking)
 * Creates minimal org doc if missing, but does not throw
 */
export async function ensureOrgDocExists(orgId: string, authUid: string): Promise<void> {
  const firestoreDb = ensureDb();
  try {
    const orgRef = doc(firestoreDb, "organizations", orgId);
    const orgSnap = await getDoc(orgRef);
    
    if (!orgSnap.exists()) {
      // Create minimal org doc
      await setDoc(
        orgRef,
        {
          name: "New Organization",
          locationText: "",
          createdAt: serverTimestamp(),
          createdBy: authUid,
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
      if (import.meta.env.DEV) console.log("[ensureOrgDocExists] Created minimal org doc:", orgId);
    }
  } catch (err) {
    // Non-blocking - just warn
    console.warn("[ensureOrgDocExists] Failed to create org doc (non-blocking):", err);
  }
}

export async function upsertOrganization(orgId: string, data: Partial<Organization>, ownerUid?: string) {
  const firestoreDb = ensureDb();
  const docRef = doc(firestoreDb, "organizations", orgId);
  const docSnap = await getDoc(docRef);
  const exists = docSnap.exists();
  
  if (exists) {
    // UPDATE: Don't include createdBy, just update fields
    await setDoc(
      docRef,
      { ...data, updatedAt: serverTimestamp() },
      { merge: true }
    );
  } else {
    // CREATE: Must include createdBy for security rules
    if (!ownerUid) {
      throw new Error('ownerUid required when creating new organization');
    }
    await setDoc(
      docRef,
      { ...data, createdBy: ownerUid, createdAt: serverTimestamp(), updatedAt: serverTimestamp() },
      { merge: false } // Explicit create, not merge
    );
  }
}

// Create or update a job owned by an organization
// Now accepts orgId directly instead of requiring full Organization object
export async function createJob(
  employerUid: string,
  orgId: string,
  payload: Omit<Job, "id" | "company" | "orgId" | "createdAt" | "updatedAt" | "createdBy">
): Promise<Job> {
  // CRITICAL: orgId is required - block if missing or invalid
  if (!orgId || typeof orgId !== 'string' || orgId.trim().length === 0) {
    throw new Error("Cannot post job: missing or invalid organization ID. Please complete employer setup.");
  }

  const firestoreDb = ensureDb();
  // Get organization name for company field
  let companyName = "";
  try {
    const orgSnap = await getDoc(doc(firestoreDb, "organizations", orgId));
    if (orgSnap.exists()) {
      companyName = (orgSnap.data() as any)?.name || "";
    }
  } catch (err) {
    console.warn("[createJob] Failed to load organization name:", err);
  }

  // Demo containment from the moment the job exists: a demo employer's job is born isDemoJob, so it
  // is never listed for real candidates — not even for the seconds before the server trigger
  // (functions/src/emergencyJobs.ts) stamps it. Read from the poster's own profile, whose flags are
  // server-owned; the trigger still corrects any mismatch.
  let demoFields: Record<string, unknown> = {};
  try {
    const me = (await getDoc(doc(firestoreDb, 'profiles', employerUid))).data() as { isDemoAccount?: unknown; demoShowcaseHidden?: unknown } | undefined;
    demoFields = {
      isDemoJob: me?.isDemoAccount === true,
      ...(me?.demoShowcaseHidden === true ? { demoShowcaseHidden: true } : {}),
    };
  } catch (err) {
    console.error('[createJob] Could not read the poster profile for demo containment; the server trigger will stamp it', err);
  }

  const finalPayload = sanitizeJobPayloadForFirestore({
    ...(payload as Record<string, unknown>),
    ...demoFields,
    company: companyName, // Set company name from org doc
    orgId: orgId, // REQUIRED: Always include orgId
    createdBy: employerUid, // Use createdBy to match Firestore rules
    isOpen: payload.isOpen !== undefined ? payload.isOpen : true,
    status: (payload as any).status || "active", // Default to "active" status
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

  const ref = await addDoc(collection(firestoreDb, "jobs"), finalPayload);

  if (import.meta.env.DEV) console.log('[JOB_WRITE]', { 
    jobId: ref.id, 
    orgId, 
    mode: 'create',
    employerUid 
  });

  return {
    id: ref.id,
    company: companyName,
    orgId: orgId,
    createdBy: employerUid,
    ...payload,
  };
}

export async function updateJob(jobId: string, data: Partial<Job>) {
  const firestoreDb = ensureDb();
  // Get existing job to preserve orgId
  const jobSnap = await getDoc(doc(firestoreDb, "jobs", jobId));
  if (!jobSnap.exists()) {
    throw new Error("Job not found");
  }
  
  const existingJob = jobSnap.data() as Job;
  const orgId = existingJob.orgId;
  
  if (!orgId) {
    throw new Error("Cannot update job: existing job missing orgId. Please contact support.");
  }
  
  // CRITICAL: Preserve orgId - never allow it to be removed
  // Remove orgId from incoming data if present (we preserve from existing)
  const { orgId: _, ...dataWithoutOrgId } = data as any;

  // `company` is a snapshot of the org name taken by createJob. Refresh it on every save so an
  // org rename reaches the job the next time the employer touches it (the onOrganizationUpdated
  // function fans a rename out to all of the org's jobs immediately; this covers the client path).
  let companyName: string | undefined;
  try {
    const orgSnap = await getDoc(doc(firestoreDb, "organizations", orgId));
    const name = orgSnap.exists() ? ((orgSnap.data() as any)?.name as string | undefined) : undefined;
    if (name && name.trim()) companyName = name.trim();
  } catch (err) {
    console.warn("[updateJob] Failed to refresh organization name:", err);
  }

  const updateData = sanitizeJobPayloadForFirestore({
    ...(dataWithoutOrgId as Record<string, unknown>),
    ...(companyName ? { company: companyName } : {}),
    orgId: orgId, // Always preserve orgId from existing job
    updatedAt: serverTimestamp(),
  }) as Record<string, unknown>;
  
  await updateDoc(doc(firestoreDb, "jobs", jobId), updateData);
  
  if (import.meta.env.DEV) console.log('[JOB_WRITE]', { 
    jobId, 
    orgId, 
    mode: 'update',
    preservedOrgId: orgId 
  });
}

/**
 * Backfill jobs missing orgId for a specific employer
 * Dev-only helper function - use with caution
 */
export async function backfillJobsOrgIdForEmployer(orgId: string, uid: string): Promise<number> {
  const firestoreDb = ensureDb();
  try {
    // Query jobs where createdBy == uid (legacy or new)
    const q = query(
      collection(firestoreDb, "jobs"),
      where("createdBy", "==", uid),
      limit(100)
    );
    
    const snap = await getDocs(q);
    const jobsToUpdate = snap.docs.filter(doc => {
      const data = doc.data();
      return !data.orgId; // Only update jobs missing orgId
    });
    
    if (jobsToUpdate.length === 0) {
      if (import.meta.env.DEV) console.log('[JOBS_ORGID_BACKFILL] fixedCount=0');
      return 0;
    }
    
    let updatedCount = 0;
    let batchCount = 0;
    const MAX_BATCH_SIZE = 500;
    const batch = writeBatch(firestoreDb);
    
    for (const jobDoc of jobsToUpdate) {
      const jobRef = doc(firestoreDb, "jobs", jobDoc.id);
      batch.update(jobRef, {
        orgId: orgId,
        updatedAt: serverTimestamp(),
      });
      batchCount++;
      updatedCount++;
      
      if (batchCount >= MAX_BATCH_SIZE) {
        await batch.commit();
        batchCount = 0;
      }
    }
    
    if (batchCount > 0) {
      await batch.commit();
    }
    
    if (import.meta.env.DEV) console.log(`[JOBS_ORGID_BACKFILL] fixedCount=${updatedCount}`);
    return updatedCount;
  } catch (err) {
    console.error('[JOBS_ORGID_BACKFILL] Error:', err);
    throw err;
  }
}

export async function getOrgJobs(orgId: string): Promise<Job[]> {
  const firestoreDb = ensureDb();
  const mapDocs = (docs: Awaited<ReturnType<typeof getDocs>>['docs']) =>
    docs.map((d) => ({ id: d.id, ...(d.data() as any) })) as Job[];

  try {
    const q = query(
      collection(firestoreDb, "jobs"),
      where("orgId", "==", orgId),
      orderBy("createdAt", "desc")
    );
    const snap = await getDocs(q);
    return mapDocs(snap.docs);
  } catch (err) {
    console.warn('[getOrgJobs] orderBy query failed, using orgId-only fallback', err);
    const q = query(collection(firestoreDb, "jobs"), where("orgId", "==", orgId));
    const snap = await getDocs(q);
    return mapDocs(snap.docs);
  }
}

// Job-level applications visible to employers
//
// 2026-10-13: one query on jobId only, sorted here (newest first, no creation time last). The
// old orderBy("createdAt") silently left out every application without a createdAt, while the
// dashboard and Jobs page count (countJobApplicants: jobId only) included them, so the applicant
// list, the Pipeline Summary and the applicant count could disagree for the same job.
function applicationCreatedAtMs(createdAt: any): number | null {
  if (!createdAt) return null;
  if (typeof createdAt.toMillis === "function") return createdAt.toMillis();
  if (typeof createdAt.seconds === "number") return createdAt.seconds * 1000;
  if (createdAt instanceof Date) return createdAt.getTime();
  if (typeof createdAt === "string") {
    const n = Date.parse(createdAt);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

export async function getJobApplications(jobId: string): Promise<JobApplication[]> {
  const firestoreDb = ensureDb();
  try {
    // Query top-level applications collection (where Cloud Function writes)
    const snap = await getDocs(query(collection(firestoreDb, "applications"), where("jobId", "==", jobId)));
    const apps = snap.docs.map((d) => {
      const data = d.data() as any;
      // Normalize status to string (handle legacy object format)
      const status = typeof data.status === 'string'
        ? data.status
        : (data.status && typeof data.status === 'object' && 'name' in data.status
          ? String(data.status.name)
          : 'new');
      return {
        id: d.id,
        ...data,
        status, // Ensure status is always a string
      };
    });
    return apps.sort((a, b) => {
      const aTime = applicationCreatedAtMs(a.createdAt);
      const bTime = applicationCreatedAtMs(b.createdAt);
      if (aTime == null && bTime == null) return a.id.localeCompare(b.id);
      if (aTime == null) return 1;
      if (bTime == null) return -1;
      return bTime - aTime;
    }) as JobApplication[];
  } catch (err: any) {
    // Thrown, not swallowed: an empty list here read as "No applicants yet" when the list had
    // simply failed to load. The applicant page shows an error with Retry instead.
    console.error('[getJobApplications] Error:', err);
    throw err;
  }
}

